import * as React from 'react';
// Import hooks.
import {
	useInputCurrency,
	UseInputCurrencyResults,
	useInputDataSelectionList,
	UseInputDataSelectionListResults,
	useInputNumber,
	UseInputNumberResults,
	useInputNumberOfStoriesSelectionList,
	UseInputNumberOfStoriesSelectionListResults,
	useInputText,
	UseInputTextResults,
} from '@app/hooks/view/input';
import {
	useInputFoundationType,
	UseInputFoundationTypeResults,
} from '@app/hooks/view';
// Import 
import {
	PropertyDomainValuesProps,
	SubformInputSettingsPropertyInfoProps,
    SubformPropertyInfoProps,
} from '@app/props';
// Import api models.
import {
	BasicPropertyInfo,
} from '@app/models/api';
// Import enumerations.
import {
	BroadLineOfBusiness,
} from '@app/enumerations';

interface UseSubformPropertyInfoParameters<
	TPropertyDomainValuesProps extends PropertyDomainValuesProps,
	TSubformInputSettingsPropertyInfoProps extends SubformInputSettingsPropertyInfoProps,
	TPropertyInfo extends BasicPropertyInfo
> {
	id: string;
	allowEdits: boolean;
	propertyInfo?: TPropertyInfo;
	broadLineOfBusinessCode: BroadLineOfBusiness;
	hasFloodPeril: boolean;
	propertyDomainValuesProps: TPropertyDomainValuesProps;
	inputSettingsProps?: TSubformInputSettingsPropertyInfoProps
	showValidationsOnMount?: boolean;
}

interface UseSubformPropertyInfoResults {
	areAllInputsDisabled: boolean;
	showBuildingUseInput: boolean;
	showConstructionTypeInput: boolean;
	showFoundationTypeInput: boolean;
	showSquareFootageInput: boolean;
	showNumberOfStoriesInput: boolean;
	showYearOfConstructionInput: boolean;
	showBuildingReplacementCostValueInput: boolean;
	showFloodZoneInput: boolean;
	isTheDesignatedFloodZoneAllowed: boolean;
	buildingUseInput: UseInputDataSelectionListResults;
	constructionTypeInput: UseInputDataSelectionListResults;
	foundationType: UseInputFoundationTypeResults;
	squareFootageInput: UseInputNumberResults;
	numberOfStoriesSelectionListInput: UseInputNumberOfStoriesSelectionListResults;
	yearOfConstructionInput: UseInputNumberResults;
	buildingReplacementCostValueInput: UseInputCurrencyResults;
	designatedFloodZoneInput: UseInputTextResults;
	applySubformPropertyInfoProps: () => SubformPropertyInfoProps;
	createPropertyInfoObject: () => BasicPropertyInfo;
	flagAllInputsToShowValidations: () => void;
	doInputsHaveChanges: () => boolean;
}

const useSubformPropertyInfo = <
	TPropertyDomainValuesProps extends PropertyDomainValuesProps,
	TSubformInputSettingsPropertyInfoProps extends SubformInputSettingsPropertyInfoProps,
	TPropertyInfo extends BasicPropertyInfo
	>(params: UseSubformPropertyInfoParameters<TPropertyDomainValuesProps, TSubformInputSettingsPropertyInfoProps, TPropertyInfo>): UseSubformPropertyInfoResults => {

	// #region exported methods

	const applySubformPropertyInfoProps = (): SubformPropertyInfoProps => {
		return {
			isTheDesignatedFloodZoneAllowed,
			showBuildingUseInput,
			showConstructionTypeInput,
			showFoundationTypeInput,
			showSquareFootageInput,
			showNumberOfStoriesInput,
			showYearOfConstructionInput,
			showBuildingReplacementCostValueInput,
			showFloodZoneInput,
			buildingUseInput,
			constructionTypeInput,
			foundationType,
			squareFootageInput,
			numberOfStoriesSelectionListInput,
			yearOfConstructionInput,
			buildingReplacementCostValueInput,
			designatedFloodZoneInput,
		};
	}

	/**
	 * Creates a property info object based on the current inputs. 
	 **/
	const createPropertyInfoObject = (): BasicPropertyInfo => {
		let propertyInfo: BasicPropertyInfo = new BasicPropertyInfo();
		propertyInfo.buildingUse = buildingUseInput.value ?? null;
		propertyInfo.constructionType = constructionTypeInput.value ?? null;
		propertyInfo.foundationTypeDetail = foundationType.foundationTypeDetailSelectionListInput.value ?? null;
		propertyInfo.squareFeet = squareFootageInput.value ?? null;
		propertyInfo.numberStoriesAboveGrade = numberOfStoriesSelectionListInput.value ?? null;
		propertyInfo.yearOfConstruction = yearOfConstructionInput.value ?? null;
		propertyInfo.buildingReplacementCostValue = buildingReplacementCostValueInput.value ?? null;
		propertyInfo.floodZone = designatedFloodZoneInput.value ?? null;

		return propertyInfo;
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		buildingUseInput.changeShowValidationFlag(true);
		constructionTypeInput.changeShowValidationFlag(true);
		foundationType.foundationTypeSelectionListInput.changeShowValidationFlag(true);
		foundationType.foundationTypeDetailSelectionListInput.changeShowValidationFlag(true);
		squareFootageInput.changeShowValidationFlag(true);
		numberOfStoriesSelectionListInput.changeShowValidationFlag(true);
		yearOfConstructionInput.changeShowValidationFlag(true);
		buildingReplacementCostValueInput.changeShowValidationFlag(true);
		designatedFloodZoneInput.changeShowValidationFlag(true);
	}

	/**
	 * Checks to see if the inputs have changes. 
	 **/
	const doInputsHaveChanges = (): boolean => {
		return buildingUseInput.doesInputHaveChanges
			|| constructionTypeInput.doesInputHaveChanges
			|| foundationType.foundationTypeDetailSelectionListInput.doesInputHaveChanges
			|| squareFootageInput.doesInputHaveChanges
			|| numberOfStoriesSelectionListInput.doesInputHaveChanges
			|| yearOfConstructionInput.doesInputHaveChanges
			|| buildingReplacementCostValueInput.doesInputHaveChanges
			|| designatedFloodZoneInput.doesInputHaveChanges;
	}

	// #endregion exported methods


	// #region private methods

	// #region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generate id for the building use. 
	 **/
	const generateIdForBuildingUse = (): string => {
		return `${generateIdForForm()}-buildingUse`;
	}

	/**
	 * Generate id for the construction type. 
	 **/
	const generateIdForConstructionType = (): string => {
		return `${generateIdForForm()}-constructionType`;
	}

	/**
	 * Generate id for the foundation type. 
	 **/
	const generateIdForFoundationType = (): string => {
		return `${generateIdForForm()}-foundationType`;
	}

	/**
	 * Generate id for the foundation type detail. 
	 **/
	const generateIdForFoundationTypeDetail = (): string => {
		return `${generateIdForForm()}-foundationTypeDetail`;
	}

	/**
	 * Generate id for the square footage. 
	 **/
	const generateIdForSquareFootage = (): string => {
		return `${generateIdForForm()}-squareFootage`;
	}

	/**
	 * Generate id for the number of stories. 
	 **/
	const generateIdForNumberOfStories = (): string => {
		return `${generateIdForForm()}-numberOfStories`;
	}

	/**
	 * Generate id for the year of construction. 
	 **/
	const generateIdForYearOfConstruction = (): string => {
		return `${generateIdForForm()}-yearOfConstruction`;
	}

	/**
	 * Generate id for building replacement cost value. 
	 **/
	const generateIdForBuildingReplacementCostValue = (): string => {
		return `${generateIdForForm()}-buildingReplacementCostValue`;
	}

	/**
	 * Generate id for flood zone. 
	 **/
	const generateIdForFloodZone = (): string => {
		return `${generateIdForForm()}-floodZone`;
	}

	// #endregion id generators

	// #region input initializers

	/**
	 * Sets up the intial value of the 'flood zone' input.
	 * @param initialFloodZoneValue
	 */
	const setupInitialFloodZoneValue = (initialFloodZoneValue: string): string => {
		return isTheDesignatedFloodZoneAllowed ? initialFloodZoneValue : String.empty();
	}

	// #endregion input initializers

	// #endregion private methods


	// #region variables

	const isTheDesignatedFloodZoneAllowed: boolean = React.useMemo(() => params.hasFloodPeril, [params.hasFloodPeril]);
	const showValidation: boolean = React.useMemo(() => params.showValidationsOnMount ?? false, [params.showValidationsOnMount]);

	// Input settings visibility.
	const showBuildingUseInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.buildingUseInput !== 'hidden',
		[params.inputSettingsProps?.buildingUseInput]
	);
	const showConstructionTypeInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.constructionTypeInput !== 'hidden',
		[params.inputSettingsProps?.constructionTypeInput]
	);
	const showFoundationTypeInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.foundationTypeInput !== 'hidden',
		[params.inputSettingsProps?.foundationTypeInput]
	);
	const showSquareFootageInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.squareFootageInput !== 'hidden',
		[params.inputSettingsProps?.squareFootageInput]
	);
	const showNumberOfStoriesInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.numberOfStoriesInput !== 'hidden',
		[params.inputSettingsProps?.numberOfStoriesInput]
	);
	const showYearOfConstructionInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.yearOfConstructionInput !== 'hidden',
		[params.inputSettingsProps?.yearOfConstructionInput]
	);
	const showBuildingReplacementCostValueInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.buildingReplacementCostValueInput !== 'hidden',
		[params.inputSettingsProps?.buildingReplacementCostValueInput]
	);
	const showFloodZoneInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.floodZoneInput !== 'hidden',
		[params.inputSettingsProps?.floodZoneInput]
	);

	// Input settings disable.
	const isBuildingUseInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits	
		|| !showBuildingUseInput
		|| params.inputSettingsProps?.buildingUseInput === 'disabled',
		[
			params.allowEdits,
			showBuildingUseInput,
			params.inputSettingsProps?.buildingUseInput
		]
	);
	const isConstructionTypeInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showConstructionTypeInput
		|| params.inputSettingsProps?.constructionTypeInput === 'disabled',
		[
			params.allowEdits,
			showConstructionTypeInput,
			params.inputSettingsProps?.constructionTypeInput
		]
	);
	const isFoundationTypeInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showFoundationTypeInput
		|| params.inputSettingsProps?.foundationTypeInput === 'disabled',
		[
			params.allowEdits,
			showFoundationTypeInput,
			params.inputSettingsProps?.foundationTypeInput
		]
	);
	const isSquareFootageInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showSquareFootageInput
		|| params.inputSettingsProps?.squareFootageInput === 'disabled',
		[
			params.allowEdits,
			showSquareFootageInput,
			params.inputSettingsProps?.squareFootageInput
		]
	);
	const isNumberOfStoriesInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showNumberOfStoriesInput
		|| params.inputSettingsProps?.numberOfStoriesInput === 'disabled',
		[
			params.allowEdits,
			showNumberOfStoriesInput,
			params.inputSettingsProps?.numberOfStoriesInput
		]
	);
	const isYearOfConstructionInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showYearOfConstructionInput
		|| params.inputSettingsProps?.yearOfConstructionInput === 'disabled',
		[
			params.allowEdits,
			showYearOfConstructionInput,
			params.inputSettingsProps?.yearOfConstructionInput
		]
	);
	const isBuildingReplacementCostValueInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showBuildingReplacementCostValueInput
		|| params.inputSettingsProps?.buildingReplacementCostValueInput === 'disabled',
		[
			params.allowEdits,
			showBuildingReplacementCostValueInput,
			params.inputSettingsProps?.buildingReplacementCostValueInput
		]
	);
	const isFloodZoneInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !isTheDesignatedFloodZoneAllowed
		|| !showFloodZoneInput
		|| params.inputSettingsProps?.floodZoneInput === 'disabled',
		[
			params.allowEdits,
			isTheDesignatedFloodZoneAllowed,
			showFloodZoneInput,
			params.inputSettingsProps?.floodZoneInput
		]
	);


	// Setup the 'building use' input hook.
	const buildingUseInput: UseInputDataSelectionListResults = useInputDataSelectionList({
		id: generateIdForBuildingUse(),
		label: 'Building Use',
		required: true,
		disabled: isBuildingUseInputDisabled,
		value: params.propertyInfo?.buildingUse ?? undefined,
		possibleDataSelections: params.propertyDomainValuesProps.possibleBuildingUses,
		showValidation,
	});

	// Setup the 'construction type' input hook.
	const constructionTypeInput: UseInputDataSelectionListResults = useInputDataSelectionList({
		id: generateIdForConstructionType(),
		label: 'Construction Type',
		required: true,
		disabled: isConstructionTypeInputDisabled,
		value: params.propertyInfo?.constructionType ?? undefined,
		possibleDataSelections: params.propertyDomainValuesProps.possibleConstructionTypes,
		showValidation,
	});

	// Setup the 'foundation type' hook.
	const foundationType: UseInputFoundationTypeResults = useInputFoundationType({
		foundationTypeSelectionListInputParameters: {
			id: generateIdForFoundationType(),
			label: 'Foundation Type',
			required: true,
			disabled: isFoundationTypeInputDisabled,
			showValidation,
			broadLineOfBusinessCode: params.broadLineOfBusinessCode,
			possibleFoundationTypes: params.propertyDomainValuesProps.possibleFoundationTypes,
		},
		foundationTypeDetailSelectionListInputParameters: {
			id: generateIdForFoundationTypeDetail(),
			label: 'Foundation Type Detail',
			required: true,
			disabled: isFoundationTypeInputDisabled,
			value: params.propertyInfo?.foundationTypeDetail ?? undefined,
			showValidation,
		},
	});

	// Setup the 'square footage' input hook.
	const squareFootageInput: UseInputNumberResults = useInputNumber({
		id: generateIdForSquareFootage(),
		label: 'Square Footage',
		required: true,
		disabled: isSquareFootageInputDisabled,
		value: params.propertyInfo?.squareFeet ?? undefined,
		minValue: 0,
		showValidation,
	});

	// Setup the 'number of stories selection list' input hook.
	const numberOfStoriesSelectionListInput: UseInputNumberOfStoriesSelectionListResults = useInputNumberOfStoriesSelectionList({
		id: generateIdForNumberOfStories(),
		label: 'Number of Stories Above Grade',
		required: true,
		disabled: isNumberOfStoriesInputDisabled,
		value: params.propertyInfo?.numberStoriesAboveGrade ?? undefined,
		showValidation: showValidation,
	});

	// Setup the 'year of construction' input hook.
	const yearOfConstructionInput: UseInputNumberResults = useInputNumber({
		id: generateIdForYearOfConstruction(),
		label: 'Year Of Construction',
		required: true,
		disabled: isYearOfConstructionInputDisabled,
		value: params.propertyInfo?.yearOfConstruction ?? undefined,
		minValue: 0,
		showValidation,
	});

	// Setup the 'building replacement cost value' input hook.
	const buildingReplacementCostValueInput: UseInputCurrencyResults = useInputCurrency({
		id: generateIdForBuildingReplacementCostValue(),
		label: 'Building Replacement Cost',
		infoText: 'Estimated Cost to build/rebuild the structure today of similar materials and quality',
		required: true,
		disabled: isBuildingReplacementCostValueInputDisabled,
		value: params.propertyInfo?.buildingReplacementCostValue ?? undefined,
		minValue: 0,
		showValidation,
	});

	// Setup the 'designated flood zone' input hook.
	const designatedFloodZoneInput: UseInputTextResults = useInputText({
		id: generateIdForFloodZone(),
		label: 'Designated Flood Zone',
		required: false,
		disabled: isFloodZoneInputDisabled,
		value: setupInitialFloodZoneValue(params.propertyInfo?.floodZone ?? String.empty()),
		showValidation,
	});

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return buildingUseInput.isDisabled
			&& constructionTypeInput.isDisabled
			&& foundationType.foundationTypeSelectionListInput.isDisabled
			&& foundationType.foundationTypeDetailSelectionListInput.isDisabled
			&& squareFootageInput.isDisabled
			&& numberOfStoriesSelectionListInput.isDisabled
			&& yearOfConstructionInput.isDisabled
			&& buildingReplacementCostValueInput.isDisabled
			&& designatedFloodZoneInput.isDisabled;
	}, [
		buildingUseInput.isDisabled,
		constructionTypeInput.isDisabled,
		foundationType.foundationTypeSelectionListInput.isDisabled,
		foundationType.foundationTypeDetailSelectionListInput.isDisabled,
		squareFootageInput.isDisabled,
		numberOfStoriesSelectionListInput.isDisabled,
		yearOfConstructionInput.isDisabled,
		buildingReplacementCostValueInput.isDisabled,
		designatedFloodZoneInput.isDisabled,
	]);

	// #endregion variables


	return {
		areAllInputsDisabled,
		showBuildingUseInput,
		showConstructionTypeInput,
		showFoundationTypeInput,
		showSquareFootageInput,
		showNumberOfStoriesInput,
		showYearOfConstructionInput,
		showBuildingReplacementCostValueInput,
		showFloodZoneInput,
		isTheDesignatedFloodZoneAllowed,
		buildingUseInput,
		constructionTypeInput,
		foundationType,
		squareFootageInput,
		numberOfStoriesSelectionListInput,
		yearOfConstructionInput,
		buildingReplacementCostValueInput,
		designatedFloodZoneInput,
		applySubformPropertyInfoProps,
		createPropertyInfoObject,
		flagAllInputsToShowValidations,
		doInputsHaveChanges,
	};
}

// Export parameters.
export {
	UseSubformPropertyInfoParameters,
}

// Export results.
export {
	UseSubformPropertyInfoResults,
}

export default useSubformPropertyInfo;